// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/components/GuildOverviewTabSettingsSection';
import styles from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/GuildOverviewTab.module.css';
import Guilds from '@app/features/guild/state/Guilds';
import type {FormInputs} from '@app/features/guild/utils/guild_tabs/GuildOverviewTabUtils';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import {WarningAlert} from '@app/features/ui/warning_alert/WarningAlert';
import {MessageNotifications} from '@fluxer/constants/src/NotificationConstants';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import type React from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {Controller} from 'react-hook-form';

const DEFAULT_NOTIFICATION_SETTINGS_DESCRIPTOR = msg({
	message: 'Default notification settings',
	comment: 'Short label in the default notifications section. Keep it concise.',
});
const ALL_MESSAGES_DESCRIPTOR = msg({
	message: 'All messages',
	comment: 'Short label in the default notifications section. Keep it concise.',
});
const MEMBERS_RECEIVE_NOTIFICATIONS_FOR_EVERY_MESSAGE_DESCRIPTOR = msg({
	message: 'Members receive notifications for every message',
	comment: 'Label in the default notifications section.',
});
const MENTIONS_ONLY_DESCRIPTOR = msg({
	message: 'Mentions only',
	comment: 'Short label in the default notifications section. Keep it concise.',
});
const MEMBERS_ARE_NOTIFIED_ONLY_WHEN_MENTIONED_DESCRIPTOR = msg({
	message: 'Members are notified only when mentioned',
	comment: 'Label in the default notifications section.',
});
export const DefaultNotificationsSection: React.FC<{
	form: UseFormReturn<FormInputs>;
	canManageGuild: boolean;
	guildId: string;
}> = ({form, canManageGuild, guildId}) => {
	const {i18n} = useLingui();
	const guild = Guilds.getGuild(guildId)!;
	return (
		<SettingsSection
			title={<Trans>Default notifications</Trans>}
			description={
				<Trans>
					Choose what new members get by default before they customize notifications. For larger communities,
					&quot;Mentions only&quot; is enforced.
				</Trans>
			}
			data-flx="guild.guild-tabs.guild-overview-tab.default-notifications-section.settings-section"
		>
			{guild.isLargeGuild && (
				<WarningAlert data-flx="guild.guild-tabs.guild-overview-tab.default-notifications-section.warning-alert">
					<Trans>
						Communities with over 250 people are forced onto the &quot;mentions only&quot; setting. Your original
						setting is preserved and will be restored if the community drops below 250 members.
					</Trans>
				</WarningAlert>
			)}
			<Controller
				name="default_message_notifications"
				control={form.control}
				render={({field}) => (
					<RadioGroup
						className={styles.notificationOptions}
						value={guild.isLargeGuild ? MessageNotifications.ONLY_MENTIONS : field.value}
						onChange={field.onChange}
						disabled={!canManageGuild || guild.isLargeGuild}
						aria-label={i18n._(DEFAULT_NOTIFICATION_SETTINGS_DESCRIPTOR)}
						options={[
							{
								value: MessageNotifications.ALL_MESSAGES,
								name: i18n._(ALL_MESSAGES_DESCRIPTOR),
								desc: i18n._(MEMBERS_RECEIVE_NOTIFICATIONS_FOR_EVERY_MESSAGE_DESCRIPTOR),
							},
							{
								value: MessageNotifications.ONLY_MENTIONS,
								name: i18n._(MENTIONS_ONLY_DESCRIPTOR),
								desc: i18n._(MEMBERS_ARE_NOTIFIED_ONLY_WHEN_MENTIONED_DESCRIPTOR),
							},
						]}
						data-flx="guild.guild-tabs.guild-overview-tab.default-notifications-section.notification-options.change"
					/>
				)}
				data-flx="guild.guild-tabs.guild-overview-tab.default-notifications-section.controller"
			/>
		</SettingsSection>
	);
};
